import { useCallback, useMemo, useState } from 'react';

import type {
  UseDateTimeBlockDatePaginationInterface,
  UseDateTimeBlockDatePaginationReturnInterface,
} from '../types/use-date-pagination.types';
import { useDateTimeBlockDisabledItems } from './use-disabled-items';

/**
 * Custom hook to handle pagination logic for DateTimeBlock.Dates component.
 *
 * Manages pagination state, calculates visible dates for current page,
 * handles navigation between pages, and provides utilities for checking
 * disabled dates.
 *
 * @param params - Configuration object containing dates, display options, and callbacks
 * @returns Object containing pagination state, computed values, and event handlers
 */
export function useDateTimeBlockDatePagination({
  dates,
  datesToShow,
  disabledDates,
  disablePagination = false,
}: UseDateTimeBlockDatePaginationInterface): UseDateTimeBlockDatePaginationReturnInterface {
  /* Pagination state */
  const [currentPage, setCurrentPage] = useState(0);

  /* Calculate pagination values */
  const totalPages = useMemo(() => {
    if (disablePagination) return 1;
    return Math.ceil(dates.length / datesToShow);
  }, [dates.length, datesToShow, disablePagination]);

  const visibleStartIndex = useMemo(() => {
    if (disablePagination) {
      return 0;
    }

    if (currentPage === totalPages - 1) {
      return Math.max(0, dates.length - datesToShow);
    }

    return currentPage * datesToShow;
  }, [currentPage, dates.length, datesToShow, disablePagination, totalPages]);

  /* Get visible dates for current page */
  const visibleDates = useMemo(() => {
    if (disablePagination) {
      return dates;
    }

    const endIndex = visibleStartIndex + datesToShow;
    return dates.slice(visibleStartIndex, endIndex);
  }, [dates, datesToShow, disablePagination, visibleStartIndex]);

  /* Use disabled items check hook for efficient disabled date checking */
  const isDateDisabled = useDateTimeBlockDisabledItems(disabledDates);

  /* Calculate if prev/next buttons should be disabled */
  const isPrevDisabled = disablePagination || currentPage === 0;
  const isNextDisabled = disablePagination || currentPage >= totalPages - 1;

  /* Navigation handlers */
  const handlePrevious = useCallback(() => {
    if (!isPrevDisabled) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
    }
  }, [isPrevDisabled, currentPage]);

  const handleNext = useCallback(() => {
    if (!isNextDisabled) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
    }
  }, [isNextDisabled, currentPage]);

  return useMemo(
    () => ({
      currentPage,
      totalPages,
      visibleStartIndex,
      visibleDates,
      isPrevDisabled,
      isNextDisabled,
      isDateDisabled,
      handlePrevious,
      handleNext,
    }),
    [
      currentPage,
      totalPages,
      visibleStartIndex,
      visibleDates,
      isPrevDisabled,
      isNextDisabled,
      isDateDisabled,
      handlePrevious,
      handleNext,
    ],
  );
}
